import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import { useLanguage } from "../i18n/LanguageContext";
import { useAvatar } from "../hooks/useAvatarContext";
import AvatarFigure from "../components/AvatarFigure";
import { ArrowLeft, Brain, Heart, BookOpen, Target, ChevronRight } from "lucide-react";
import axios from "axios";

const TimelinePage = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const avatar = useAvatar();
  const navigate = useNavigate();
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/avatar/timeline`);
        setTimeline(res.data.timeline || []);
      } catch {
        setTimeline([]);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  const avatarState = avatar?.avatarState;
  const color = avatarState?.avatar_color || user?.avatar_color || "#002FA7";
  const state = avatarState?.state || "dormant";
  const score = avatarState?.clarity_score || 0;

  const typeIcon = {
    consultation: <Brain className="w-4 h-4" />,
    mood: <Heart className="w-4 h-4" />,
    diary: <BookOpen className="w-4 h-4" />,
    challenge: <Target className="w-4 h-4" />,
  };

  const typeLabel = {
    consultation: t("timeline_type_consultation"),
    mood: t("timeline_type_mood"),
    diary: t("timeline_type_diary"),
    challenge: t("timeline_type_challenge"),
  };

  const typeColor = {
    consultation: "#002FA7",
    mood: "#EC4899",
    diary: "#8B5CF6",
    challenge: "#10B981",
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString(undefined, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
    } catch {
      return dateStr.slice(0, 10);
    }
  };

  // Group by day
  const grouped = {};
  timeline.forEach((item) => {
    const day = (item.date || "").slice(0, 10);
    if (!grouped[day]) grouped[day] = [];
    grouped[day].push(item);
  });
  const days = Object.keys(grouped).sort().reverse();

  return (
    <div className="min-h-screen bg-[#F5F5F7]" data-testid="timeline-page">
      {/* Header */}
      <div className="bg-white border-b border-[#E5E5EA] sticky top-0 z-30">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center gap-4">
          <button onClick={() => navigate(-1)} className="text-[#6B7280] hover:text-[#111111]" data-testid="timeline-back">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="font-['Outfit'] text-lg font-semibold text-[#111111]">{t("timeline_title")}</h1>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-6">
        {/* Avatar status card */}
        <div className="bg-white rounded-2xl p-5 mb-6 border border-[#E5E5EA] flex items-center gap-5" data-testid="timeline-avatar-card">
          <AvatarFigure state={state} color={color} size={80} interactive={false} />
          <div className="flex-1">
            <p className="font-['Outfit'] font-semibold text-[#111111] text-base">{t(`avatar_state_${state}`)}</p>
            <div className="flex items-center gap-3 mt-2">
              <div className="flex-1 h-2 bg-[#F5F5F7] rounded-full overflow-hidden">
                <div className="h-full rounded-full transition-all duration-700" style={{ width: `${Math.max(score, 2)}%`, backgroundColor: color }} />
              </div>
              <span className="text-sm font-semibold" style={{ color }}>{score}/100</span>
            </div>
            <p className="text-xs text-[#6B7280] mt-1">{t(`avatar_insight_${state}`)}</p>
          </div>
        </div>

        {/* Timeline */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-2 border-[#002FA7] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : days.length === 0 ? (
          <div className="text-center py-12" data-testid="timeline-empty">
            <AvatarFigure state="dormant" color={color} size={120} interactive={false} className="mx-auto mb-4" />
            <p className="text-[#6B7280] text-base">{t("timeline_empty")}</p>
            <p className="text-[#6B7280]/50 text-sm mt-1">{t("timeline_empty_hint")}</p>
          </div>
        ) : (
          <div className="relative" data-testid="timeline-list">
            {/* Vertical line */}
            <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-[#E5E5EA]" />

            {days.map((day) => (
              <div key={day} className="mb-6">
                {/* Day header */}
                <div className="flex items-center gap-3 mb-3 relative">
                  <div className="w-3 h-3 rounded-full bg-[#111111] z-10 ml-[18px]" />
                  <span className="text-xs font-semibold text-[#111111] uppercase tracking-wide bg-[#F5F5F7] px-2">
                    {new Date(day + "T12:00:00").toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" })}
                  </span>
                </div>

                {/* Events for this day */}
                <div className="ml-14 space-y-2">
                  {grouped[day].map((item, idx) => (
                    <div
                      key={`${day}-${idx}`}
                      className="bg-white rounded-xl p-4 border border-[#E5E5EA] hover:border-[#D1D5DB] transition-all cursor-default"
                      onClick={() => {
                        if (item.type === "consultation" && item.id) navigate(`/history/${item.id}`);
                      }}
                      style={item.type === "consultation" && item.id ? { cursor: "pointer" } : {}}
                      data-testid={`timeline-item-${item.type}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${typeColor[item.type]}15`, color: typeColor[item.type] }}>
                          {typeIcon[item.type]}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-semibold" style={{ color: typeColor[item.type] }}>{typeLabel[item.type]}</span>
                            <span className="text-[10px] text-[#6B7280]">{formatDate(item.date)}</span>
                          </div>
                          {item.title && <p className="text-sm text-[#111111] mt-0.5 truncate">{item.title}</p>}
                          {item.type === "mood" && item.detail && (
                            <div className="flex gap-3 mt-1">
                              <span className="text-xs text-[#6B7280]">{t("mood_label")}: {item.detail.mood}/5</span>
                              <span className="text-xs text-[#6B7280]">{t("energy_label")}: {item.detail.energy}/5</span>
                              <span className="text-xs text-[#6B7280]">{t("stress_label")}: {item.detail.stress}/5</span>
                            </div>
                          )}
                          {item.type === "consultation" && item.detail && (
                            <span className="text-xs font-semibold text-[#10B981]">{item.detail}</span>
                          )}
                        </div>
                        {item.type === "consultation" && item.id && (
                          <ChevronRight className="w-4 h-4 text-[#6B7280]" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TimelinePage;
